import { AddIcon, RestaurantIcon } from "../components/icons";
import { ProductName, ProductPrice } from "../components/ProductsList";
import useGoTo from "../hooks/useGoTo";
import useQuickAdd from "../hooks/useQuickAdd";
import useCartUpsell from "../hooks/useCartUpsell";
import toPriceDisplay from "../utils/toPriceDisplay";

/**
 * "Peça também" na sacola: complementos para o que falta no pedido (bebida,
 * sobremesa, acompanhamento), com o "+" que adiciona direto quando não há
 * nada para escolher. As sugestões vêm do useCartUpsell (regras em
 * utils/upsell.js); a versão do totem é Selfcheckout/CartUpsellTotem.
 *
 * Abaixo do pedido mínimo da entrega, avisa quanto falta.
 */
export default function CartUpsell() {
  const goTo = useGoTo();
  const { canQuickAdd, quickAdd } = useQuickAdd();
  const { title, suggestions, belowMinimum, missing } = useCartUpsell({
    canQuickAdd,
  });

  if (!suggestions.length) {
    return null;
  }

  return (
    <section className="upsell" aria-labelledby="upsell-title">
      <h2 id="upsell-title" className="checkout-section__title upsell__title">
        {title}
      </h2>
      {belowMinimum && (
        <p className="upsell__hint">
          Faltam {toPriceDisplay(missing)} para o pedido mínimo da entrega
        </p>
      )}
      <ul className="highlights__track">
        {suggestions.map((product) => {
          // a tela do produto marca o item como vindo do "Peça também"
          const open = () => goTo(`/product/${product.id}`, { upsell: true });
          return (
            <li key={product.id}>
              <UpsellCard
                product={product}
                onOpen={open}
                onAdd={
                  canQuickAdd(product)
                    ? () => quickAdd(product, { upsell: true })
                    : open
                }
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function UpsellCard({ product, onOpen, onAdd }) {
  return (
    <div
      className="highlight-card highlight-card--compact"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {/* sem foto, um bloco neutro: os cards lado a lado ficam alinhados */}
      <div className="highlight-card__media">
        {product.pictureUrl ? (
          <img
            loading="lazy"
            decoding="async"
            src={product.pictureUrl}
            alt=""
            width="132"
            height="96"
          />
        ) : (
          <span className="highlight-card__placeholder">
            <RestaurantIcon />
          </span>
        )}
      </div>
      <div className="highlight-card__body">
        <ProductName product={product} />
        <div className="highlight-card__footer">
          <ProductPrice product={product} />
          <button
            type="button"
            className="add-button"
            onClick={(event) => {
              event.stopPropagation();
              onAdd();
            }}
            aria-label={`Adicionar ${product.name}`}
          >
            <AddIcon />
          </button>
        </div>
      </div>
    </div>
  );
}
